const SUGGESTED_QUESTION_REGEX = /<q>([\s\S]*?)<\/q>/gi;

function stripIncompleteQuestionTag(text: string): string {
  const lastOpen = text.lastIndexOf('<q>');
  if (lastOpen === -1) return text;

  const tail = text.slice(lastOpen);
  if (tail.includes('</q>')) return text;

  return text.slice(0, lastOpen);
}

export const SUGGESTED_QUESTION_LINK_PREFIX = '#aida-suggested:';

export type BotMessageSegment = { type: 'markdown'; content: string } | { type: 'question'; content: string };

export function normalizeSuggestedQuestionLineBreaks(text: string): string {
  return text
    .replace(/\r\n/g, '\n')
    .replace(/[ \t]*\n[ \t]*<q>/gi, ' <q>')
    .replace(/<\/q>[ \t]*\n[ \t]*<q>/gi, '</q> <q>');
}

export function transformSuggestedQuestionTags(text: string): string {
  const normalized = normalizeSuggestedQuestionLineBreaks(text);
  const regex = new RegExp(SUGGESTED_QUESTION_REGEX.source, 'gi');

  const withLinks = normalized.replace(regex, (_, question: string) => {
    const trimmed = question.trim();
    if (!trimmed) return '';

    const href = `${SUGGESTED_QUESTION_LINK_PREFIX}${encodeURIComponent(trimmed)}`;
    return `[${trimmed}](${href})`;
  });

  return stripIncompleteQuestionTag(withLinks);
}

export function isSuggestedQuestionLink(href?: string): href is string {
  return typeof href === 'string' && href.startsWith(SUGGESTED_QUESTION_LINK_PREFIX);
}

export function decodeSuggestedQuestionLink(href: string): string {
  return decodeURIComponent(href.slice(SUGGESTED_QUESTION_LINK_PREFIX.length));
}

export function splitBotMessageSegments(text: string): BotMessageSegment[] {
  const segments: BotMessageSegment[] = [];
  const regex = new RegExp(SUGGESTED_QUESTION_REGEX.source, 'gi');
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) !== null) {
    const before = text.slice(lastIndex, match.index);
    if (before) segments.push({ type: 'markdown', content: before });

    const question = match[1].trim();
    if (question) segments.push({ type: 'question', content: question });

    lastIndex = match.index + match[0].length;
  }

  const remainder = stripIncompleteQuestionTag(text.slice(lastIndex));
  if (remainder) segments.push({ type: 'markdown', content: remainder });

  return segments;
}

export type ParsedBotMessage = {
  body: string;
  questions: string[];
};

export function parseSuggestedQuestions(text: string): ParsedBotMessage {
  const segments = splitBotMessageSegments(text);
  return {
    body: segments
      .filter(
        (segment): segment is Extract<BotMessageSegment, { type: 'markdown' }> => segment.type === 'markdown',
      )
      .map(segment => segment.content)
      .join('')
      .trim(),
    questions: segments
      .filter(
        (segment): segment is Extract<BotMessageSegment, { type: 'question' }> => segment.type === 'question',
      )
      .map(segment => segment.content),
  };
}
